import React, { useMemo, useCallback, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import CrosshairPlugin from 'chartjs-plugin-crosshair';
import AnnotationPlugin from 'chartjs-plugin-annotation';
import ZoomPlugin from 'chartjs-plugin-zoom';
import { useTheme, ThemeToggleButton } from '../../hooks/useTheme';
import 'chartjs-adapter-date-fns';
import { useIsMobile } from '../../hooks/useIsMobile';

// Register Chart.js components and plugins
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler,
  CrosshairPlugin,
  AnnotationPlugin,
  ZoomPlugin
);

const EnhancedPortfolioChart = ({ 
  data, 
  onLoad, 
  onError, 
  isExpanded = false,
  showControls = true,
  enableZoom = true,
  enableCrosshair = true
}) => {
  const chartRef = useRef(null);
  const { isDark, getThemeColors, getChartColorPalette } = useTheme();
  
  // State for interactive features
  const [timeRange, setTimeRange] = React.useState('7D');
  const [chartType, setChartType] = React.useState('area');
  const [showStats, setShowStats] = React.useState(false);
  const isMobile = useIsMobile();
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  

  // Get theme colors for chart styling
  const themeColors = getThemeColors();
  const chartColorPalette = getChartColorPalette();

  // Calculate portfolio statistics
  const portfolioStats = useMemo(() => {
    // Handle both data.datasets and data.data.datasets structures
    const datasets = data?.datasets || data?.data?.datasets;
    if (!datasets?.[0]?.data) return null;
    
    const values = datasets[0].data;
    const currentValue = values[values.length - 1];
    const previousValue = values[values.length - 2] || values[0];
    const firstValue = values[0];
    
    const dayChange = currentValue - previousValue;
    const dayChangePercent = previousValue ? ((dayChange / previousValue) * 100) : 0;
    const totalChange = currentValue - firstValue;
    const totalChangePercent = firstValue ? ((totalChange / firstValue) * 100) : 0;
    
    const maxValue = Math.max(...values);
    const minValue = Math.min(...values);
    
    return {
      currentValue,
      dayChange,
      dayChangePercent,
      totalChange,
      totalChangePercent,
      maxValue,
      minValue,
      volatility: calculateVolatility(values)
    };
  }, [data]);

  // Calculate volatility (simplified standard deviation)
  const calculateVolatility = (values) => {
    if (values.length < 2) return 0;
    const returns = [];
    for (let i = 1; i < values.length; i++) {
      returns.push((values[i] - values[i-1]) / values[i-1]);
    }
    const mean = returns.reduce((sum, r) => sum + r, 0) / returns.length;
    const variance = returns.reduce((sum, r) => sum + Math.pow(r - mean, 2), 0) / returns.length;
    return Math.sqrt(variance) * 100; // Convert to percentage
  };

  // Generate gradient for chart fill
  const createGradient = useCallback((ctx, chartArea) => {
    if (!chartArea) return null;
    
    const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
    
    if (isDark) {
      // Dark mode gradient - bright cyan
      gradient.addColorStop(0, 'rgba(34, 211, 238, 0.4)');
      gradient.addColorStop(0.5, 'rgba(34, 211, 238, 0.2)');
      gradient.addColorStop(1, 'rgba(34, 211, 238, 0.05)');
    } else {
      // Light mode gradient - emerald green
      gradient.addColorStop(0, 'rgba(16, 185, 129, 0.3)');
      gradient.addColorStop(0.5, 'rgba(16, 185, 129, 0.15)');
      gradient.addColorStop(1, 'rgba(16, 185, 129, 0.05)');
    }
    
    return gradient;
  }, [isDark]);

  // Enhanced chart data with professional styling
  const enhancedChartData = useMemo(() => {
    // Handle both data.datasets and data.data.datasets structures
    const datasets = data?.datasets || data?.data?.datasets;
    if (!datasets?.[0]) return { datasets: [] };
    
    const baseDataset = datasets[0];
    const chartLabels = data?.labels || data?.data?.labels;
    const borderColor = isDark ? '#22d3ee' : '#10b981'; // Cyan for dark, emerald for light
    
    return {
      labels: chartLabels,
      datasets: [{
        ...baseDataset,
        borderColor,
        backgroundColor: (context) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;
          if (!chartArea) return null;
          return createGradient(ctx, chartArea);
        },
        borderWidth: 3,
        tension: 0.4,
        fill: true,
        pointRadius: 6,
        pointHoverRadius: 8,
        pointBackgroundColor: isDark ? '#1e293b' : '#ffffff',
        pointBorderColor: borderColor,
        pointBorderWidth: 2,
        pointHoverBackgroundColor: borderColor,
        pointHoverBorderColor: isDark ? '#1e293b' : '#ffffff',
        pointHoverBorderWidth: 3,
        // Add glow effect
        shadowColor: borderColor,
        shadowBlur: 10,
        shadowOffsetX: 0,
        shadowOffsetY: 0
      }]
    };
  }, [data, isDark, createGradient]);

  // Professional chart options with plugins
  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    devicePixelRatio: window.devicePixelRatio || 2, // High DPI support
    animation: {
      duration: 1000,
      easing: 'easeInOutCubic',
      onComplete: () => {
        if (onLoad) onLoad();
      }
    },
    layout: {
      padding: {
        top: 20,
        bottom: 20,
        left: 10,
        right: 10
      }
    },
    interaction: {
      intersect: false,
      mode: 'index'
    },
    plugins: {
      legend: {
        display: false // We'll create custom legend
      },
      tooltip: {
        enabled: true,
        backgroundColor: isDark ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255, 255, 255, 0.95)',
        titleColor: themeColors.text,
        bodyColor: themeColors.text,
        borderColor: themeColors.border,
        borderWidth: 1,
        cornerRadius: 12,
        padding: 16,
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          size: 13
        },
        displayColors: false,
        callbacks: {
          title: (tooltipItems) => {
            return `${data.labels?.[tooltipItems[0].dataIndex] || ''}`;
          },
          label: (context) => {
            const value = context.parsed.y;
            const prevValue = context.dataIndex > 0 ? 
              context.dataset.data[context.dataIndex - 1] : value;
            const change = value - prevValue;
            const changePercent = prevValue ? ((change / prevValue) * 100) : 0;
            
            return [
              `Value: $${value.toLocaleString('en-US', { 
                minimumFractionDigits: 2, 
                maximumFractionDigits: 2 
              })}`,
              `Change: ${change >= 0 ? '+' : ''}$${change.toFixed(2)} (${changePercent >= 0 ? '+' : ''}${changePercent.toFixed(2)}%)`
            ];
          }
        }
      },
      crosshair: enableCrosshair ? {
        line: {
          color: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
          width: 1,
          dashPattern: [5, 5]
        },
        sync: {
          enabled: false
        },
        zoom: {
          enabled: false
        }
      } : false,
      zoom: enableZoom ? {
        pan: {
          enabled: true,
          mode: 'x',
          modifierKey: isMobile ? null : 'ctrl'
        },
        zoom: {
          wheel: {
            enabled: !isMobile
          },
          pinch: {
            enabled: isMobile
          },
          mode: 'x',
          limits: {
            x: { minRange: 2 }
          }
        }
      } : false,
      annotation: {
        annotations: {
          // Add trend line if we have enough data points
          ...(enhancedChartData.datasets[0]?.data?.length > 5 && {
            trendLine: {
              type: 'line',
              scaleID: 'y',
              value: portfolioStats?.currentValue || 0,
              borderColor: isDark ? 'rgba(34, 211, 238, 0.5)' : 'rgba(16, 185, 129, 0.5)',
              borderWidth: 2,
              borderDash: [10, 5],
              label: {
                content: `Current: $${portfolioStats?.currentValue?.toLocaleString() || 0}`,
                enabled: true,
                position: 'end',
                backgroundColor: isDark ? 'rgba(15, 23, 42, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                color: themeColors.text,
                font: {
                  size: 11,
                  weight: 'bold'
                }
              }
            }
          })
        }
      }
    },
    scales: {
      x: {
        grid: {
          color: themeColors.grid,
          drawBorder: false,
          lineWidth: 1
        },
        ticks: {
          color: themeColors.textSecondary,
          font: {
            size: 11,
            weight: '500'
          },
          maxTicksLimit: isMobile ? 4 : 7
        }
      },
      y: {
        position: 'right', // Professional look
        grid: {
          color: themeColors.grid,
          drawBorder: false,
          lineWidth: 1
        },
        ticks: {
          color: themeColors.textSecondary,
          font: {
            size: 11,
            weight: '500'
          },
          callback: (value) => {
            return `$${value.toLocaleString('en-US', { 
              minimumFractionDigits: 0, 
              maximumFractionDigits: 0 
            })}`;
          },
          maxTicksLimit: isMobile ? 5 : 8
        }
      }
    }
  }), [isDark, themeColors, isMobile, enableCrosshair, enableZoom, enhancedChartData, portfolioStats, onLoad]);

  // Format currency for display
  const formatCurrency = (value, compact = false) => {
    if (!value && value !== 0) return '$0.00';
    
    if (compact && Math.abs(value) >= 1000) {
      if (Math.abs(value) >= 1000000) {
        return `$${(value / 1000000).toFixed(1)}M`;
      }
      return `$${(value / 1000).toFixed(1)}K`;
    }
    
    return value.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Format percentage
  const formatPercentage = (value) => {
    if (!value && value !== 0) return '0.00%';
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
  };

  // Time range options
  const timeRanges = [
    { key: '1D', label: '1D' },
    { key: '1W', label: '1W' },
    { key: '1M', label: '1M' },
    { key: '3M', label: '3M' },
    { key: '1Y', label: '1Y' },
    { key: 'ALL', label: 'All' }
  ];

  // Reset zoom function
  const resetZoom = useCallback(() => {
    if (chartRef.current) {
      chartRef.current.resetZoom();
    }
  }, []);

  // Handle both data.datasets and data.data.datasets structures for validation
  const datasets = data?.datasets || data?.data?.datasets;
  if (!datasets?.[0]?.data || datasets[0].data.length === 0) {
    return (
      <div className="enhanced-portfolio-chart-error">
        <div className="error-content">
          <span className="error-icon">📊</span>
          <p>No portfolio data available</p>
          <small>Unable to generate chart visualization</small>
        </div>
      </div>
    );
  }

  return (
    <div className={`enhanced-portfolio-chart ${isFullscreen ? 'fullscreen' : ''} ${isDark ? 'dark' : 'light'}`}>
      {/* Chart Header with Controls */}
      {showControls && (
        <div className="chart-header">
          <div className="chart-header-left">
            <h3 className="chart-title">
              {data.title || 'Portfolio Performance'}
              {portfolioStats && (
                <span className={`performance-badge ${portfolioStats.dayChangePercent >= 0 ? 'positive' : 'negative'}`}>
                  {formatPercentage(portfolioStats.dayChangePercent)}
                </span>
              )}
            </h3>
            {data.subtitle && (
              <p className="chart-subtitle">{data.subtitle}</p>
            )}
          </div>
          
          <div className="chart-header-right">
            <div className="chart-controls">
              {/* Time Range Selector */}
              <div className="time-range-selector">
                {timeRanges.map(range => (
                  <button
                    key={range.key}
                    className={`time-range-btn ${timeRange === range.key ? 'active' : ''}`}
                    onClick={() => setTimeRange(range.key)}
                  >
                    {range.label}
                  </button>
                ))}
              </div>
              
              {/* Chart Type Toggle */}
              <div className="chart-type-selector">
                <button
                  className={`chart-type-btn ${chartType === 'line' ? 'active' : ''}`}
                  onClick={() => setChartType('line')}
                  title="Line Chart"
                >
                  📈
                </button>
                <button
                  className={`chart-type-btn ${chartType === 'area' ? 'active' : ''}`}
                  onClick={() => setChartType('area')}
                  title="Area Chart"
                >
                  📊
                </button>
              </div>
              
              {/* Additional Controls */}
              <button
                className="control-btn"
                onClick={() => setShowStats(!showStats)}
                title="Toggle Statistics"
              >
                📈
              </button>
              
              {enableZoom && (
                <button
                  className="control-btn"
                  onClick={resetZoom}
                  title="Reset Zoom"
                >
                  🔄
                </button>
              )}
              
              <ThemeToggleButton size="small" className="chart-theme-toggle" />
              
              {isMobile && (
                <button
                  className="control-btn fullscreen-toggle"
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  title="Toggle Fullscreen"
                >
                  {isFullscreen ? '⤢' : '⤡'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Portfolio Statistics Panel */}
      {showStats && portfolioStats && (
        <div className="portfolio-stats">
          <div className="stat-item">
            <span className="stat-label">Current Value</span>
            <span className="stat-value">{formatCurrency(portfolioStats.currentValue)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Day Change</span>
            <span className={`stat-value ${portfolioStats.dayChange >= 0 ? 'positive' : 'negative'}`}>
              {formatCurrency(portfolioStats.dayChange)} ({formatPercentage(portfolioStats.dayChangePercent)})
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Total Change</span>
            <span className={`stat-value ${portfolioStats.totalChange >= 0 ? 'positive' : 'negative'}`}>
              {formatCurrency(portfolioStats.totalChange)} ({formatPercentage(portfolioStats.totalChangePercent)})
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Volatility</span>
            <span className="stat-value">{portfolioStats.volatility.toFixed(2)}%</span>
          </div>
        </div>
      )}

      {/* Chart Canvas */}
      <div className="chart-canvas-container">
        <Line
          ref={chartRef}
          data={enhancedChartData}
          options={chartOptions}
          className="portfolio-chart-canvas"
        />
      </div>

      {/* Chart Footer */}
      {data.metadata && (
        <div className="chart-footer">
          <div className="metadata-info">
            {data.metadata.timeframe && (
              <span className="metadata-item">
                <span className="metadata-icon">⏱</span>
                {data.metadata.timeframe}
              </span>
            )}
            {data.metadata.source && (
              <span className="metadata-item">
                <span className="metadata-icon">📊</span>
                {data.metadata.source}
              </span>
            )}
            {data.metadata.lastUpdated && (
              <span className="metadata-item">
                <span className="metadata-icon">🔄</span>
                Updated: {new Date(data.metadata.lastUpdated).toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedPortfolioChart;